import { useCallback, useEffect, useRef } from 'react'

export type DebouncedCallback<A extends any[]> = ((...args: A) => void) & {
	clear: () => void
	flush: () => void | Promise<unknown>
}

export const useDebounceCallback = <T, A extends any[]>(
	value: (...args: A) => T,
	delay = 100,
): DebouncedCallback<A> => {
	const callback = useRef<(...args: A) => T>()
	const callArgs = useRef<A>()
	const timeout = useRef<ReturnType<typeof setTimeout>>()

	useEffect(
		() => () => (timeout.current ? clearTimeout(timeout.current) : undefined),
		[],
	)

	callback.current = value

	const clear = useCallback(() => {
		if (timeout.current) {
			clearTimeout(timeout.current)
			timeout.current = undefined
		}
	}, [])

	const flush = useCallback(() => {
		if (!timeout.current) {
			return
		}
		clearTimeout(timeout.current)
		timeout.current = undefined
		if (callback.current && callArgs.current) {
			return callback.current.apply(null, callArgs.current)
		}
	}, [])

	const schedule = useCallback(
		(...args: A) => {
			callArgs.current = args

			if (timeout.current) {
				clearTimeout(timeout.current)
			}

			timeout.current = setTimeout(() => {
				timeout.current = undefined
				if (callback.current && callArgs.current) {
					callback.current.apply(null, callArgs.current)
				}
			}, delay)
		},
		[delay],
	) as DebouncedCallback<A>

	schedule.flush = flush
	schedule.clear = clear

	return schedule
}
